import {
  useValidateMacroplanogramCapacityQuery,
  ValidateMacroplanogramCapacityInput,
} from '@graphql/generated/graphql';

/**
 * Wraps the generated ValidateMacroplanogramCapacity query. Capacity validation
 * is a standalone call (no longer part of GetPlanDiff): callers construct the
 * itemsToAdd / itemsToDelete deltas from a plan diff and fire this to learn the
 * macroplanogram's total fixture volume vs. the projected occupied volume.
 *
 * The query runs whenever this hook is used. Feature gating (GFC-only) is done
 * by the caller via conditional rendering, so the hook stays a plain query
 * wrapper with no gating responsibility of its own.
 */
const useValidateMacroplanogramCapacity = (input: ValidateMacroplanogramCapacityInput) => {
  const { data, isLoading, isFetching, isError, isSuccess, error, refetch } = useValidateMacroplanogramCapacityQuery(
    { input },
    {
      staleTime: 0,
      cacheTime: 0,
      // Keep the prior result visible while a re-check (new query key) is in
      // flight, so the banner doesn't flash to "unavailable" between fetches.
      keepPreviousData: true,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  return {
    capacityValidation: data?.validateMacroplanogramCapacity?.capacityValidation ?? undefined,
    isLoading,
    // True whenever a fetch is in flight, including re-checks where
    // keepPreviousData holds `isLoading` at false. Drives the "Re-checking
    // capacity…" indicator.
    isFetching,
    isError,
    isSuccess,
    error,
    refetch,
  };
};

export default useValidateMacroplanogramCapacity;
